import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useInitiative, useAdminInitiatives } from '@/features/project-initiatives/hooks'
import { useUpdateAdminInitiative, useCreateAdminInitiative, useAdminDomains, useAdminPriorities } from '@/features/project-initiatives/hooks'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useToast } from '@/hooks/use-toast'
import { useGenerateInitiative } from '@/features/ai/hooks'
import { motion, type Variants } from 'framer-motion'
import {
    ArrowLeft,
    ArrowRight,
    Clock,
    Users,
    DollarSign,
    TrendingUp,
    Zap,
    Shield,
    BarChart3,
    Wrench,
    Target,
    CheckCircle,
    AlertTriangle,
    Layers,
    Pencil,
    Save,
    X,
    ChevronsUpDown,
    Plus,
    Wand2,
    Loader2,
    Paperclip,
    ChevronDown,
} from 'lucide-react'
import { InitiativeDetailContent, InitiativeBadgeRow } from '@/features/results/components/initiative-detail-content'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'



const sectionVariants: Variants = {
    hidden: { opacity: 0, y: 12 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: 0.1 + i * 0.05, duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
    }),
}

// ── Editable Tag Section ──────────────────────────────────────────────
interface TagSectionProps {
    title: string
    items: string[]
    icon: React.ElementType
    variant?: 'secondary' | 'outline' | 'default'
    index: number
    isEditing?: boolean
    onUpdate?: (items: string[]) => void
}

function TagSection({ title, items, icon: Icon, variant = 'secondary', index, isEditing, onUpdate }: TagSectionProps) {
    const [newTag, setNewTag] = useState('')

    if (!isEditing && (!items || items.length === 0)) return null

    const addTag = () => {
        if (newTag.trim() && onUpdate) {
            onUpdate([...items, newTag.trim()])
            setNewTag('')
        }
    }

    const removeTag = (idx: number) => {
        if (onUpdate) {
            onUpdate(items.filter((_, i) => i !== idx))
        }
    }

    return (
        <motion.div custom={index} variants={sectionVariants} initial="hidden" animate="visible">
            <Card>
                <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">{title}</h3>
                        <span className="text-xs text-muted-foreground">({items?.length || 0})</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {(items || []).map((item, idx) => (
                            <Badge key={idx} variant={variant} className="text-xs">
                                {item}
                                {isEditing && (
                                    <button type="button" onClick={() => removeTag(idx)} className="ml-1 hover:text-destructive">×</button>
                                )}
                            </Badge>
                        ))}
                    </div>
                    {isEditing && (
                        <div className="flex gap-2 mt-2">
                            <Input
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                placeholder={`Add ${title.toLowerCase()}...`}
                                className="h-7 text-xs"
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag() } }}
                            />
                            <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addTag}>Add</Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    )
}

// ── Main Page ─────────────────────────────────────────────────────────
const EMPTY_INITIATIVE = {
    title: '',
    short_description: '',
    description: '',
    domain: '',
    priority: 'medium',
    risk_level: 'medium',
    from_maturity_level: 1,
    to_maturity_level: 2,
    impact_level: 'medium',
    effort_level: 'medium',
    duration_weeks: 4,
    team_size_min: 1,
    team_size_max: 3,
    budget_range_min: 0,
    budget_range_max: 0,
    business_value: '',
    roi_estimate: '',
    required_technologies: [],
    required_skills: [],
    recommended_tools: [],
    success_metrics: [],
    kpis: [],
    prerequisites: [],
    dependencies: [],
    risk_factors: [],
    mitigation_strategies: [],
    phases: [],
}

export function SettingsInitiativeDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { toast } = useToast()
    const isNew = id === 'new'
    const [searchParams] = useSearchParams()
    const startInEditMode = searchParams.get('edit') === 'true'
    const { data: initiative, isLoading, error } = useInitiative(isNew ? '' : (id || ''))
    const updateMutation = useUpdateAdminInitiative()
    const createMutation = useCreateAdminInitiative()
    const { data: domains = [] } = useAdminDomains()
    const { data: priorities = [] } = useAdminPriorities()
    const { data: allInitiativesData } = useAdminInitiatives({ per_page: 200 })

    const [isEditing, setIsEditing] = useState(isNew || startInEditMode)
    const [form, setForm] = useState<any>(isNew ? { ...EMPTY_INITIATIVE } : null)
    const formInitializedRef = React.useRef(false)

    // AI Fill state
    const [showAiPanel, setShowAiPanel] = useState(false)
    const [aiPrompt, setAiPrompt] = useState('')
    const [aiFiles, setAiFiles] = useState<File[]>([])
    const aiGen = useGenerateInitiative()

    // Auto-apply AI result to form when it arrives
    useEffect(() => {
        if (aiGen.result && form) {
            setForm((prev: any) => ({
                ...prev,
                title: aiGen.result.title || prev.title,
                short_description: aiGen.result.short_description || prev.short_description,
                description: aiGen.result.description || prev.description,
                domain: aiGen.result.domain || prev.domain,
                priority: aiGen.result.priority || prev.priority,
                risk_level: aiGen.result.risk_level || prev.risk_level,
                from_maturity_level: parseInt(aiGen.result.from_maturity_level) || prev.from_maturity_level,
                to_maturity_level: parseInt(aiGen.result.to_maturity_level) || prev.to_maturity_level,
                impact_level: aiGen.result.impact_level || prev.impact_level,
                effort_level: aiGen.result.effort_level || prev.effort_level,
                duration_weeks: parseInt(aiGen.result.duration_weeks) || prev.duration_weeks,
                team_size_min: parseInt(aiGen.result.team_size_min) || prev.team_size_min,
                team_size_max: parseInt(aiGen.result.team_size_max) || prev.team_size_max,
                budget_range_min: parseInt(aiGen.result.budget_range_min) || prev.budget_range_min,
                budget_range_max: parseInt(aiGen.result.budget_range_max) || prev.budget_range_max,
                business_value: aiGen.result.business_value || prev.business_value,
                roi_estimate: aiGen.result.roi_estimate || prev.roi_estimate,
                required_technologies: aiGen.result.required_technologies || prev.required_technologies,
                required_skills: aiGen.result.required_skills || prev.required_skills,
                recommended_tools: aiGen.result.recommended_tools || prev.recommended_tools,
                success_metrics: aiGen.result.success_metrics || prev.success_metrics,
                kpis: aiGen.result.kpis || prev.kpis,
                prerequisites: aiGen.result.prerequisites || prev.prerequisites,
                risk_factors: aiGen.result.risk_factors || prev.risk_factors,
                mitigation_strategies: aiGen.result.mitigation_strategies || prev.mitigation_strategies,
                phases: aiGen.result.phases || prev.phases,
            }))
            toast({ title: 'AI Fill Applied', description: 'Form fields have been populated. Review and save.' })
            setShowAiPanel(false)
            aiGen.reset()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiGen.result])

    // Populate form when initiative data loads or after exiting edit mode
    useEffect(() => {
        if (!isNew && initiative) {
            if (!isEditing || !formInitializedRef.current) {
                setForm({ ...initiative })
                formInitializedRef.current = true
            }
        }
    }, [initiative, isEditing, isNew])

    const startEditing = () => {
        setForm(isNew ? { ...EMPTY_INITIATIVE } : { ...initiative })
        setIsEditing(true)
    }

    const cancelEditing = () => {
        if (isNew) {
            navigate('/settings/recommendations')
            return
        }
        setForm({ ...initiative })
        setIsEditing(false)
    }

    const saveChanges = () => {
        if (!form) return
        const payload = {
            ...form,
            dependencies: (form.dependencies || []).map((d: any) => d.id),
        }

        if (isNew) {
            createMutation.mutate(payload, {
                onSuccess: (response: any) => {
                    toast({ title: 'Success', description: 'Initiative created successfully.' })
                    const newId = response?.data?.initiative?.id
                    if (newId) {
                        navigate(`/settings/recommendations/initiatives/${newId}`, { replace: true })
                    } else {
                        navigate('/settings/recommendations')
                    }
                },
                onError: (error: any) => {
                    toast({ title: 'Error', description: error.response?.data?.error || 'Failed to create initiative.', variant: 'destructive' })
                }
            })
        } else {
            if (!id) return
            updateMutation.mutate({ id, data: payload }, {
                onSuccess: () => {
                    toast({ title: 'Success', description: 'Initiative updated successfully.' })
                    setIsEditing(false)
                },
                onError: (error: any) => {
                    toast({ title: 'Error', description: error.response?.data?.error || 'Failed to update initiative.', variant: 'destructive' })
                }
            })
        }
    }

    const updateField = (field: string, value: any) => {
        setForm((prev: any) => ({ ...prev, [field]: value }))
    }

    if (!isNew && isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
            </div>
        )
    }

    if (!isNew && (error || !initiative)) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
                <AlertTriangle className="h-10 w-10 text-muted-foreground/40" />
                <p className="text-muted-foreground">Initiative not found or failed to load.</p>
                <Button variant="outline" onClick={() => navigate('/settings/recommendations')}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Recommendations
                </Button>
            </div>
        )
    }

    // Use form data in edit mode, initiative data in view mode
    const data = isEditing && form ? form : initiative
    if (!data || (isEditing && !form)) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
            </div>
        )
    }

    let sectionIndex = 0

    return (
        <motion.div
            className="space-y-6 max-w-5xl"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const }}
        >
            {/* Top Bar */}
            <div className="flex items-center justify-between">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/settings/recommendations')}
                    className="text-muted-foreground hover:text-foreground -ml-2"
                >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Recommendation Engine
                </Button>

                <div className="flex gap-2">
                    {isEditing ? (
                        <>
                            <Button variant="outline" size="sm" onClick={cancelEditing} disabled={isNew ? createMutation.isPending : updateMutation.isPending}>
                                <X className="h-4 w-4 mr-1" />
                                Cancel
                            </Button>
                            <Button size="sm" onClick={saveChanges} disabled={isNew ? createMutation.isPending : updateMutation.isPending}>
                                <Save className="h-4 w-4 mr-1" />
                                {isNew
                                    ? (createMutation.isPending ? 'Creating...' : 'Create Initiative')
                                    : (updateMutation.isPending ? 'Saving...' : 'Save Changes')
                                }
                            </Button>
                        </>
                    ) : (
                        <AuthorizationGate actions={['update_initiative']}>
                            <Button variant="outline" size="sm" onClick={startEditing}>
                                <Pencil className="h-4 w-4 mr-1" />
                                Edit
                            </Button>
                        </AuthorizationGate>
                    )}
                </div>
            </div>

            {/* AI Fill Panel — shown in edit mode */}
            {isEditing && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.2 }}
                >
                    <Card className="border-dashed border-primary/30 bg-primary/5">
                        <CardContent className="p-4">
                            <button
                                type="button"
                                className="flex items-center gap-2 w-full text-left text-sm font-medium text-primary"
                                onClick={() => setShowAiPanel(!showAiPanel)}
                            >
                                <Wand2 className="h-4 w-4" />
                                Fill with AI
                                <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${showAiPanel ? 'rotate-180' : ''}`} />
                            </button>

                            {showAiPanel && (
                                <div className="mt-3 space-y-3">
                                    <Textarea
                                        value={aiPrompt}
                                        onChange={(e) => setAiPrompt(e.target.value)}
                                        placeholder="Describe the initiative you want to create, e.g. 'Implement zero-trust security architecture for cloud workloads'..."
                                        rows={3}
                                        className="text-sm"
                                    />

                                    {/* File attachments */}
                                    <div className="flex items-center gap-3">
                                        <Label htmlFor="ai-files" className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors">
                                            <Paperclip className="h-3.5 w-3.5" />
                                            Attach files
                                        </Label>
                                        <input
                                            id="ai-files"
                                            type="file"
                                            multiple
                                            accept=".txt,.md,.pdf,.doc,.docx"
                                            className="hidden"
                                            onChange={(e) => setAiFiles(Array.from(e.target.files || []))}
                                        />
                                        {aiFiles.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {aiFiles.map((f, i) => (
                                                    <Badge key={i} variant="secondary" className="text-[10px]">
                                                        {f.name}
                                                        <button type="button" onClick={() => setAiFiles(prev => prev.filter((_, j) => j !== i))} className="ml-1 hover:text-destructive">×</button>
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <Button
                                        className="w-full"
                                        size="sm"
                                        disabled={!aiPrompt.trim() || aiGen.isRunning}
                                        onClick={() => {
                                            const formData = new FormData()
                                            formData.append('prompt', aiPrompt)
                                            aiFiles.forEach((f) => formData.append('files', f))
                                            aiGen.run(formData)
                                        }}
                                    >
                                        {aiGen.isRunning ? (
                                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {aiGen.status === 'generating' ? 'Generating...' : 'Analyzing...'}</>
                                        ) : (
                                            <><Wand2 className="h-4 w-4 mr-2" /> Generate Initiative</>
                                        )}
                                    </Button>

                                    {/* Success message */}
                                    {aiGen.status === 'done' && (
                                        <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                                            <span className="text-sm text-green-700 dark:text-green-400 font-medium">✓ Initiative generated and applied to form</span>
                                        </div>
                                    )}

                                    {aiGen.error && (
                                        <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/20 p-3 rounded">
                                            {aiGen.error}
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>
            )}

            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
            >
                <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2 flex-1">
                        {isEditing ? (
                            <>
                                <Input
                                    value={form.title}
                                    onChange={(e) => updateField('title', e.target.value)}
                                    className="text-2xl font-bold h-auto py-1 px-2"
                                    placeholder="Title"
                                />
                                <Input
                                    value={form.short_description}
                                    onChange={(e) => updateField('short_description', e.target.value)}
                                    className="text-sm text-gray-600 h-auto py-1 px-2"
                                    placeholder="Brief description"
                                />
                            </>
                        ) : (
                            <>
                                <h1 className="text-2xl font-bold text-gray-900">{data.title}</h1>
                                <p className="text-gray-600 max-w-2xl">{data.short_description}</p>
                            </>
                        )}
                    </div>
                    {isEditing ? (
                        <div className="flex items-center gap-2 shrink-0 pt-2">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={form.is_active}
                                onChange={(e) => updateField('is_active', e.target.checked)}
                                className="h-4 w-4 rounded border-input"
                            />
                            <Label htmlFor="is_active" className="text-xs">Active</Label>
                        </div>
                    ) : (
                        <Badge variant={data.is_active ? 'default' : 'secondary'} className="text-xs shrink-0">
                            {data.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                    )}
                </div>

                {/* Tag Badges / Editable Selects */}
                {isEditing ? (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
                        <div>
                            <Label className="text-xs text-muted-foreground">Domain</Label>
                            <Select value={form.domain} onValueChange={(v) => updateField('domain', v)}>
                                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {domains.filter(Boolean).map((d: string) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Priority</Label>
                            <Select value={form.priority} onValueChange={(v) => updateField('priority', v)}>
                                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {priorities.filter(Boolean).map((p: string) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">From Level</Label>
                            <Select value={form.from_maturity_level?.toString()} onValueChange={(v) => updateField('from_maturity_level', parseInt(v))}>
                                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[1, 2, 3, 4, 5].map(l => <SelectItem key={l} value={l.toString()}>Level {l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">To Level</Label>
                            <Select value={form.to_maturity_level?.toString()} onValueChange={(v) => updateField('to_maturity_level', parseInt(v))}>
                                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[1, 2, 3, 4, 5].map(l => <SelectItem key={l} value={l.toString()}>Level {l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Risk Level</Label>
                            <Select value={form.risk_level} onValueChange={(v) => updateField('risk_level', v)}>
                                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                ) : (
                    <div className="mt-3">
                        <InitiativeBadgeRow data={data} />
                    </div>
                )}
            </motion.div>

            {/* ── View-mode body: shared component ─────────────── */}
            {!isEditing && (
                <InitiativeDetailContent data={data} />
            )}

            {/* ── Edit-mode body: page-specific forms ──────────────── */}
            {isEditing && (
                <>
                    {/* Description */}
                    <motion.div custom={sectionIndex++} variants={sectionVariants} initial="hidden" animate="visible">
                        <Card>
                            <CardContent className="p-4">
                                <h3 className="text-sm font-semibold mb-2">Description</h3>
                                <Textarea
                                    value={form.description}
                                    onChange={(e) => updateField('description', e.target.value)}
                                    rows={4}
                                    placeholder="Detailed description..."
                                />
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Key Metrics Grid */}
                    <motion.div
                        className="grid grid-cols-2 md:grid-cols-4 gap-4"
                        custom={sectionIndex++}
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <Card>
                            <CardContent className="p-4 text-center">
                                <Clock className="h-5 w-5 mx-auto mb-1.5 text-blue-500" />
                                <Input type="number" min="1" value={form.duration_weeks} onChange={(e) => updateField('duration_weeks', parseInt(e.target.value))} className="text-center h-8 text-lg font-bold" />
                                <div className="text-xs text-muted-foreground">weeks</div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 text-center">
                                <Users className="h-5 w-5 mx-auto mb-1.5 text-purple-500" />
                                <div className="flex gap-1 items-center justify-center">
                                    <Input type="number" min="1" value={form.team_size_min} onChange={(e) => updateField('team_size_min', parseInt(e.target.value))} className="text-center h-8 w-14 text-sm font-bold" />
                                    <span className="text-xs">–</span>
                                    <Input type="number" min="1" value={form.team_size_max} onChange={(e) => updateField('team_size_max', parseInt(e.target.value))} className="text-center h-8 w-14 text-sm font-bold" />
                                </div>
                                <div className="text-xs text-muted-foreground">team members</div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 text-center">
                                <TrendingUp className="h-5 w-5 mx-auto mb-1.5 text-green-500" />
                                <Select value={form.impact_level} onValueChange={(v) => updateField('impact_level', v)}>
                                    <SelectTrigger className="h-8 text-sm font-bold"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="high">High</SelectItem>
                                        <SelectItem value="medium">Medium</SelectItem>
                                        <SelectItem value="low">Low</SelectItem>
                                    </SelectContent>
                                </Select>
                                <div className="text-xs text-muted-foreground">impact</div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4 text-center">
                                <Zap className="h-5 w-5 mx-auto mb-1.5 text-orange-500" />
                                <Select value={form.effort_level} onValueChange={(v) => updateField('effort_level', v)}>
                                    <SelectTrigger className="h-8 text-sm font-bold"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="high">High</SelectItem>
                                        <SelectItem value="medium">Medium</SelectItem>
                                        <SelectItem value="low">Low</SelectItem>
                                    </SelectContent>
                                </Select>
                                <div className="text-xs text-muted-foreground">effort</div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Budget, Business Value, ROI */}
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-3 gap-4"
                        custom={sectionIndex++}
                        variants={sectionVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                                    <h3 className="text-sm font-semibold">Budget Range</h3>
                                </div>
                                <div className="flex gap-2 items-center">
                                    <Input type="number" min="0" value={form.budget_range_min || ''} onChange={(e) => updateField('budget_range_min', e.target.value ? parseInt(e.target.value) : null)} placeholder="Min (k)" className="h-7 text-xs" />
                                    <span className="text-xs">–</span>
                                    <Input type="number" min="0" value={form.budget_range_max || ''} onChange={(e) => updateField('budget_range_max', e.target.value ? parseInt(e.target.value) : null)} placeholder="Max (k)" className="h-7 text-xs" />
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                                    <h3 className="text-sm font-semibold">Business Value</h3>
                                </div>
                                <Textarea value={form.business_value || ''} onChange={(e) => updateField('business_value', e.target.value)} rows={2} placeholder="Describe business value..." className="text-xs" />
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                                    <h3 className="text-sm font-semibold">ROI Estimate</h3>
                                </div>
                                <Input value={form.roi_estimate || ''} onChange={(e) => updateField('roi_estimate', e.target.value)} placeholder="e.g., 200-300% within 12 months" className="h-7 text-xs" />
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Tag Sections */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TagSection title="Required Technologies" items={form.required_technologies || []} icon={Wrench} variant="secondary" index={sectionIndex++}
                            isEditing onUpdate={(items) => updateField('required_technologies', items)} />
                        <TagSection title="Required Skills" items={form.required_skills || []} icon={Target} variant="outline" index={sectionIndex++}
                            isEditing onUpdate={(items) => updateField('required_skills', items)} />
                        <TagSection title="Recommended Tools" items={form.recommended_tools || []} icon={Wrench} variant="secondary" index={sectionIndex++}
                            isEditing onUpdate={(items) => updateField('recommended_tools', items)} />
                        <TagSection title="Success Metrics" items={form.success_metrics || []} icon={CheckCircle} variant="outline" index={sectionIndex++}
                            isEditing onUpdate={(items) => updateField('success_metrics', items)} />
                        <TagSection title="KPIs" items={form.kpis || []} icon={BarChart3} variant="secondary" index={sectionIndex++}
                            isEditing onUpdate={(items) => updateField('kpis', items)} />
                        <TagSection title="Prerequisites" items={form.prerequisites || []} icon={Layers} variant="outline" index={sectionIndex++}
                            isEditing onUpdate={(items) => updateField('prerequisites', items)} />
                        <TagSection title="Mitigation Strategies" items={form.mitigation_strategies || []} icon={Shield} variant="secondary" index={sectionIndex++}
                            isEditing onUpdate={(items) => updateField('mitigation_strategies', items)} />
                        <TagSection title="Risk Factors" items={form.risk_factors || []} icon={AlertTriangle} variant="outline" index={sectionIndex++}
                            isEditing onUpdate={(items) => updateField('risk_factors', items)} />

                        {/* Dependencies — relational, uses dropdown */}
                        <motion.div custom={sectionIndex++} variants={sectionVariants} initial="hidden" animate="visible">
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Layers className="h-4 w-4 text-muted-foreground" />
                                        <h3 className="text-sm font-semibold">Dependencies</h3>
                                        <span className="text-xs text-muted-foreground">({(form.dependencies || []).length})</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {(form.dependencies || []).map((dep: any) => (
                                            <Badge key={dep.id} variant="secondary" className="text-xs">
                                                {dep.title}
                                                <button type="button" onClick={() => {
                                                    updateField('dependencies', (form.dependencies || []).filter((d: any) => d.id !== dep.id))
                                                }} className="ml-1 hover:text-destructive">×</button>
                                            </Badge>
                                        ))}
                                    </div>
                                    <div className="mt-2">
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" size="sm" className="w-full justify-between h-8 text-xs font-normal">
                                                    Add dependency...
                                                    <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-[350px] p-0" align="start">
                                                <Command>
                                                    <CommandInput placeholder="Search initiatives..." className="h-8" />
                                                    <CommandList>
                                                        <CommandEmpty>No initiatives found.</CommandEmpty>
                                                        <CommandGroup className="max-h-[200px] overflow-auto">
                                                            {(allInitiativesData?.initiatives || []).filter((i: any) => {
                                                                const existingIds = (form.dependencies || []).map((d: any) => d.id)
                                                                return i.id !== id && !existingIds.includes(i.id)
                                                            }).map((i: any) => (
                                                                <CommandItem
                                                                    key={i.id}
                                                                    value={i.title}
                                                                    onSelect={() => {
                                                                        updateField('dependencies', [...(form.dependencies || []), { id: i.id, title: i.title }])
                                                                    }}
                                                                >
                                                                    {i.title}
                                                                </CommandItem>
                                                            ))}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </div>

                    {/* Phases */}
                    <motion.div custom={sectionIndex++} variants={sectionVariants} initial="hidden" animate="visible">
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <Layers className="h-4 w-4 text-muted-foreground" />
                                        <h3 className="text-sm font-semibold">Phases</h3>
                                        <span className="text-xs text-muted-foreground">({(form.phases || []).length})</span>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-xs"
                                        onClick={() => {
                                            const phases = [...(form.phases || [])]
                                            phases.push({ name: '', description: '', duration_weeks: 1 })
                                            updateField('phases', phases)
                                        }}
                                    >
                                        <Plus className="h-3 w-3 mr-1" />
                                        Add Phase
                                    </Button>
                                </div>
                                <div className="space-y-3">
                                    {(form.phases || []).length === 0 && (
                                        <p className="text-sm text-muted-foreground text-center py-4">No phases defined.</p>
                                    )}
                                    {(form.phases || []).map((phase: any, idx: number) => (
                                        <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                                            <div className="space-y-2">
                                                <div className="flex items-start justify-between gap-2">
                                                    <span className="flex items-center justify-center h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-semibold shrink-0">{idx + 1}</span>
                                                    <div className="flex-1 grid grid-cols-[1fr_auto] gap-2">
                                                        <Input
                                                            value={phase.name || ''}
                                                            onChange={(e) => {
                                                                const phases = [...(form.phases || [])]
                                                                phases[idx] = { ...phases[idx], name: e.target.value }
                                                                updateField('phases', phases)
                                                            }}
                                                            placeholder="Phase name"
                                                            className="h-7 text-xs font-medium"
                                                        />
                                                        <div className="flex items-center gap-1">
                                                            <Input
                                                                type="number"
                                                                min={1}
                                                                value={phase.duration_weeks || ''}
                                                                onChange={(e) => {
                                                                    const phases = [...(form.phases || [])]
                                                                    phases[idx] = { ...phases[idx], duration_weeks: parseInt(e.target.value) || 0 }
                                                                    updateField('phases', phases)
                                                                }}
                                                                placeholder="0"
                                                                className="h-7 text-xs w-16"
                                                            />
                                                            <span className="text-xs text-muted-foreground whitespace-nowrap">weeks</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-0.5">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 w-7 p-0"
                                                            disabled={idx === 0}
                                                            onClick={() => {
                                                                const phases = [...(form.phases || [])]
                                                                    ;[phases[idx - 1], phases[idx]] = [phases[idx], phases[idx - 1]]
                                                                updateField('phases', phases)
                                                            }}
                                                        >
                                                            <ArrowRight className="h-3 w-3 -rotate-90" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 w-7 p-0"
                                                            disabled={idx === (form.phases || []).length - 1}
                                                            onClick={() => {
                                                                const phases = [...(form.phases || [])]
                                                                    ;[phases[idx], phases[idx + 1]] = [phases[idx + 1], phases[idx]]
                                                                updateField('phases', phases)
                                                            }}
                                                        >
                                                            <ArrowRight className="h-3 w-3 rotate-90" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                                            onClick={() => {
                                                                const phases = [...(form.phases || [])]
                                                                phases.splice(idx, 1)
                                                                updateField('phases', phases)
                                                            }}
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                </div>
                                                <Textarea
                                                    value={phase.description || ''}
                                                    onChange={(e) => {
                                                        const phases = [...(form.phases || [])]
                                                        phases[idx] = { ...phases[idx], description: e.target.value }
                                                        updateField('phases', phases)
                                                    }}
                                                    placeholder="Phase description..."
                                                    rows={2}
                                                    className="text-xs resize-none"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                </>
            )}

            {/* Metadata */}
            {!isNew && initiative && (
                <motion.div className="text-xs text-muted-foreground/60 pb-4" custom={sectionIndex} variants={sectionVariants} initial="hidden" animate="visible">
                    {initiative.created_by_username && <>Created by: {initiative.created_by_username} · </>}
                    Created: {new Date(initiative.created_at).toLocaleDateString()} · Updated: {new Date(initiative.updated_at).toLocaleDateString()}
                </motion.div>
            )}

            {/* Bottom Action Buttons */}
            <div className="flex justify-end gap-2 pb-6 pt-2 !mt-0 border-t">
                {isEditing ? (
                    <>
                        <Button variant="outline" size="sm" onClick={cancelEditing} disabled={isNew ? createMutation.isPending : updateMutation.isPending}>
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                        </Button>
                        <Button size="sm" onClick={saveChanges} disabled={isNew ? createMutation.isPending : updateMutation.isPending}>
                            <Save className="h-4 w-4 mr-1" />
                            {isNew
                                ? (createMutation.isPending ? 'Creating...' : 'Create Initiative')
                                : (updateMutation.isPending ? 'Saving...' : 'Save Changes')
                            }
                        </Button>
                    </>
                ) : (
                    <AuthorizationGate actions={['update_initiative']}>
                        <Button variant="outline" size="sm" onClick={startEditing}>
                            <Pencil className="h-4 w-4 mr-1" />
                            Edit
                        </Button>
                    </AuthorizationGate>
                )}
            </div>
        </motion.div>
    )
}
